import React from 'react';
import { ArrowLeft, Users, Award, Clock, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const translations = {
  back: {
    sk: 'Späť na hlavnú stránku',
    en: 'Back to homepage',
    de: 'Zurück zur Startseite'
  },
  title: {
    sk: 'O nás',
    en: 'About Us',
    de: 'Über uns'
  },
  subtitle: {
    sk: 'WorkPro je súčasťou ALS Group',
    en: 'WorkPro is part of ALS Group',
    de: 'WorkPro ist Teil der ALS Group'
  },
  description: {
    sk: `WorkPro je personálna agentúra špecializujúca sa na dočasné zamestnávanie, recruitment a HR poradenstvo. Sme súčasťou medzinárodnej skupiny ALS Group, ktorá pôsobí v oblasti personálnych služieb už viac ako 4 roky.

Naším poslaním je spájať kvalifikovaných pracovníkov so spoľahlivými zamestnávateľmi. Veríme, že správni ľudia na správnom mieste sú základom úspechu každej firmy.

Pôsobíme na Slovensku, v Českej republike, Poľsku a Maďarsku. Naši klienti sú prevažne výrobné podniky, logistické centrá a firmy z oblasti služieb.`,
    en: `WorkPro is a staffing agency specializing in temporary employment, recruitment, and HR consulting. We are part of the international ALS Group, which has been operating in the personnel services sector for over 4 years.

Our mission is to connect qualified workers with reliable employers. We believe that the right people in the right place are the foundation of every company's success.

We operate in Slovakia, Czech Republic, Poland, and Hungary. Our clients are mainly manufacturing companies, logistics centers, and service companies.`,
    de: `WorkPro ist eine Personalagentur, die sich auf Zeitarbeit, Rekrutierung und HR-Beratung spezialisiert hat. Wir sind Teil der internationalen ALS Group, die seit über 4 Jahren im Bereich Personaldienstleistungen tätig ist.

Unsere Mission ist es, qualifizierte Arbeitnehmer mit zuverlässigen Arbeitgebern zu verbinden. Wir glauben, dass die richtigen Menschen am richtigen Ort die Grundlage für den Erfolg jedes Unternehmens sind.

Wir sind in der Slowakei, Tschechien, Polen und Ungarn tätig. Unsere Kunden sind hauptsächlich Fertigungsunternehmen, Logistikzentren und Dienstleistungsunternehmen.`
  },
  stats: {
    sk: [
      { number: '4+', label: 'Roky skúseností' },
      { number: '500+', label: 'Obsadených pozícií ročne' },
      { number: '100+', label: 'Spokojných klientov' },
      { number: '4', label: 'Krajiny pôsobenia' }
    ],
    en: [
      { number: '4+', label: 'Years of experience' },
      { number: '500+', label: 'Positions filled annually' },
      { number: '100+', label: 'Satisfied clients' },
      { number: '4', label: 'Countries of operation' }
    ],
    de: [
      { number: '4+', label: 'Jahre Erfahrung' },
      { number: '500+', label: 'Jährlich besetzte Positionen' },
      { number: '100+', label: 'Zufriedene Kunden' },
      { number: '4', label: 'Tätigkeitsländer' }
    ]
  },
  values: {
    title: {
      sk: 'Naše hodnoty',
      en: 'Our values',
      de: 'Unsere Werte'
    },
    list: {
      sk: [
        { icon: Users, title: 'Ľudia na prvom mieste', description: 'Každý kandidát aj klient je pre nás dôležitý. Pristupujeme k nim individuálne a s rešpektom.' },
        { icon: Award, title: 'Kvalita', description: 'Dodávame len overených a kvalifikovaných pracovníkov. Za našou prácou si stojíme.' },
        { icon: Clock, title: 'Rýchlosť', description: 'Chápeme, že čas sú peniaze. Reagujeme promptne a dodržiavame termíny.' },
        { icon: Building2, title: 'Partnerstvo', description: 'Budujeme dlhodobé vzťahy založené na dôvere a vzájomnej spokojnosti.' }
      ],
      en: [
        { icon: Users, title: 'People first', description: 'Every candidate and client is important to us. We treat them individually and with respect.' },
        { icon: Award, title: 'Quality', description: 'We deliver only verified and qualified workers. We stand behind our work.' },
        { icon: Clock, title: 'Speed', description: 'We understand that time is money. We respond promptly and meet deadlines.' },
        { icon: Building2, title: 'Partnership', description: 'We build long-term relationships based on trust and mutual satisfaction.' }
      ],
      de: [
        { icon: Users, title: 'Menschen zuerst', description: 'Jeder Kandidat und Kunde ist uns wichtig. Wir behandeln sie individuell und mit Respekt.' },
        { icon: Award, title: 'Qualität', description: 'Wir liefern nur verifizierte und qualifizierte Arbeitskräfte. Wir stehen hinter unserer Arbeit.' },
        { icon: Clock, title: 'Geschwindigkeit', description: 'Wir verstehen, dass Zeit Geld ist. Wir reagieren prompt und halten Termine ein.' },
        { icon: Building2, title: 'Partnerschaft', description: 'Wir bauen langfristige Beziehungen auf Vertrauen und gegenseitiger Zufriedenheit auf.' }
      ]
    }
  },
  cta: {
    title: {
      sk: 'Chcete sa stať naším partnerom?',
      en: 'Want to become our partner?',
      de: 'Möchten Sie unser Partner werden?'
    },
    button: {
      sk: 'Kontaktujte nás',
      en: 'Contact us',
      de: 'Kontaktieren Sie uns'
    }
  }
};

const AboutPage = () => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();

  const stats = translations.stats[language];
  const values = translations.values.list[language];

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#E8A238] pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#131132]/70 hover:text-[#131132] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {translations.back[language]}
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#131132] mb-4">
            {translations.title[language]}
          </h1>
          
          <p className="text-xl md:text-2xl text-[#131132]/70 max-w-3xl">
            {translations.subtitle[language]}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Description */}
        <div className="mb-16">
          <p className="text-lg text-[#131132]/70 leading-relaxed max-w-4xl whitespace-pre-line">
            {translations.description[language]}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="text-4xl md:text-5xl font-bold text-[#E8A238] mb-2">{stat.number}</div>
              <div className="text-[#131132]/60">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-[#131132] mb-8">{translations.values.title[language]}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="flex items-start gap-4 p-6 bg-white rounded-2xl shadow-sm">
                  <div className="w-12 h-12 bg-[#E8A238]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#E8A238]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#131132] mb-2">{value.title}</h3>
                    <p className="text-[#131132]/60">{value.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#131132] rounded-3xl p-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {translations.cta.title[language]}
          </h2>
          <button
            onClick={openContactModal}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#E8A238] text-[#131132] font-semibold rounded-xl hover:bg-[#D97706] transition-colors"
          >
            {translations.cta.button[language]}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
